import { Switch } from '../components/ui';
import { formatINR, formatINRCompact, formatRate } from '../lib/format';

export type PricingMode = 'PER_STUDENT' | 'LUMP_SUM';

export interface PricingDraft {
  /** True when this school is priced by hand rather than at the plan's list price. */
  custom: boolean;
  /** Which shape the negotiated deal takes. Only meaningful when custom is true. */
  mode: PricingMode;
  /** Rupees per student per month, as typed. Converted to paise on submit. */
  rupees: string;
  /** Rupees per year for the whole school, as typed. Converted to paise on submit. */
  lumpSumRupees: string;
  note: string;
}

export const emptyPricingDraft: PricingDraft = {
  custom: false,
  mode: 'PER_STUDENT',
  rupees: '',
  lumpSumRupees: '',
  note: '',
};

/** Rupees typed by a human → integer paise. Returns null for blank/invalid input. */
export function draftToPaise(draft: PricingDraft): number | null {
  if (!draft.custom || draft.mode !== 'PER_STUDENT') return null;
  const value = Number(draft.rupees);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
}

/** Same conversion, for the flat annual figure. Returns null outside lump-sum mode. */
export function draftToLumpSumPaise(draft: PricingDraft): number | null {
  if (!draft.custom || draft.mode !== 'LUMP_SUM') return null;
  const value = Number(draft.lumpSumRupees);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
}

export function paiseToRupeeString(paise: number | null): string {
  if (paise === null) return '';
  return (paise / 100).toFixed(paise % 100 === 0 ? 0 : 2);
}

/**
 * The per-school rate control: either ₹ per student per month, negotiated or
 * inherited from the plan, or a flat ₹ per year for the whole school. Shows
 * the resulting bill as you type, because "₹22" and "₹22 × 1,540 × 12 =
 * ₹4,06,560/year" land very differently — and a lump sum needs the same
 * translation the other way round.
 */
export function PricingFields({
  draft,
  onChange,
  planName,
  planListMonthlyPaise,
  students,
  billingCycle,
}: {
  draft: PricingDraft;
  onChange: (next: PricingDraft) => void;
  planName: string;
  /** The plan's list price per student per month. Null for a custom-priced plan. */
  planListMonthlyPaise: number | null;
  students: number;
  billingCycle: 'ANNUAL' | 'MONTHLY';
}) {
  const mustBeCustom = planListMonthlyPaise === null;
  const isCustom = draft.custom || mustBeCustom;
  const isLumpSum = isCustom && draft.mode === 'LUMP_SUM';

  const customPaise = draftToPaise(draft);
  const lumpSumPaise = draftToLumpSumPaise(draft);

  const effectivePaise = isLumpSum ? null : (customPaise ?? planListMonthlyPaise);

  const annualTotal = isLumpSum
    ? lumpSumPaise
    : effectivePaise === null
      ? null
      : effectivePaise * students * 12;
  const monthlyTotal = annualTotal === null ? null : Math.round(annualTotal / 12);

  // A lump sum has no per-student rate of its own — this is only for
  // comparing it against the plan's list price, on the same footing.
  const derivedMonthlyPaise =
    isLumpSum && lumpSumPaise !== null && students > 0 ? Math.round(lumpSumPaise / 12 / students) : null;

  const deltaPct =
    !isLumpSum && customPaise !== null && planListMonthlyPaise
      ? ((customPaise - planListMonthlyPaise) / planListMonthlyPaise) * 100
      : null;

  return (
    <div
      style={{
        border: '1px solid var(--rule)',
        borderRadius: 'var(--r-input)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        background: 'var(--white)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="module-row__name">Negotiated pricing</div>
          <div className="module-row__desc">
            {mustBeCustom ? (
              <>
                <strong>{planName}</strong> has no list price — set a rate or a lump sum for this school.
              </>
            ) : (
              <>
                {planName} list price is{' '}
                <strong>{formatRate(planListMonthlyPaise)}</strong> per student per month.
              </>
            )}
          </div>
        </div>
        <Switch
          checked={isCustom}
          disabled={mustBeCustom}
          onChange={(next) => onChange({ ...draft, custom: next })}
          label="Price this school by hand"
        />
      </div>

      {isCustom ? (
        <>
          <div className="segmented" style={{ alignSelf: 'flex-start' }}>
            {(['PER_STUDENT', 'LUMP_SUM'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={draft.mode === option}
                onClick={() => onChange({ ...draft, mode: option })}
                style={{ padding: '8px 14px' }}
              >
                {option === 'PER_STUDENT' ? 'Rate per student' : 'Lump sum per year'}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12 }}>
            {draft.mode === 'PER_STUDENT' ? (
              <label className="field">
                <span>₹ per student / month</span>
                <input
                  inputMode="decimal"
                  value={draft.rupees}
                  autoFocus
                  placeholder={planListMonthlyPaise ? paiseToRupeeString(planListMonthlyPaise) : '25'}
                  onChange={(e) => onChange({ ...draft, rupees: e.target.value.replace(/[^\d.]/g, '') })}
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 600 }}
                />
              </label>
            ) : (
              <label className="field">
                <span>₹ per year · whole school</span>
                <input
                  inputMode="decimal"
                  value={draft.lumpSumRupees}
                  autoFocus
                  placeholder="500000"
                  onChange={(e) => onChange({ ...draft, lumpSumRupees: e.target.value.replace(/[^\d.]/g, '') })}
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 600 }}
                />
              </label>
            )}
            <label className="field">
              <span>Why (kept on the record)</span>
              <input
                value={draft.note}
                placeholder="Negotiated by Ratnesh · 3-year commitment"
                onChange={(e) => onChange({ ...draft, note: e.target.value })}
              />
            </label>
          </div>

          {deltaPct !== null ? (
            <div className="caption">
              {deltaPct === 0 ? (
                'Same as the list price.'
              ) : (
                <>
                  <span style={{ color: deltaPct < 0 ? 'var(--leaf)' : 'var(--marigold-deep)', fontWeight: 700 }}>
                    {deltaPct < 0 ? `${Math.abs(deltaPct).toFixed(1)}% discount` : `${deltaPct.toFixed(1)}% above list`}
                  </span>{' '}
                  against the {planName} list price.
                </>
              )}
            </div>
          ) : null}

          {isLumpSum && derivedMonthlyPaise !== null && planListMonthlyPaise ? (
            <div className="caption">
              Works out to <strong>{formatRate(derivedMonthlyPaise)}/student/month</strong> at today's head
              count — for comparison only, the school is billed the flat amount regardless of student count.
            </div>
          ) : null}
        </>
      ) : null}

      <div
        style={{
          display: 'flex',
          gap: 24,
          borderTop: '1px solid var(--rule)',
          paddingTop: 12,
          flexWrap: 'wrap',
        }}
      >
        {isLumpSum ? (
          <Total label="Rate" value={lumpSumPaise === null ? 'Not set' : `${formatINRCompact(lumpSumPaise)}/year flat`} />
        ) : (
          <Total label="Rate" value={effectivePaise === null ? 'Not set' : `${formatRate(effectivePaise)}/student/mo`} />
        )}
        <Total label="Students" value={students > 0 ? students.toLocaleString('en-IN') : '—'} />
        <Total
          label="Per month"
          value={monthlyTotal === null || (!isLumpSum && students === 0) ? '—' : formatINRCompact(monthlyTotal)}
        />
        <Total
          label="Per year"
          value={annualTotal === null || (!isLumpSum && students === 0) ? '—' : formatINRCompact(annualTotal)}
          emphasis
        />
        <Total
          label={billingCycle === 'ANNUAL' ? 'Each invoice (annual)' : 'Each invoice (monthly)'}
          value={
            monthlyTotal === null || annualTotal === null || (!isLumpSum && students === 0)
              ? '—'
              : formatINR(billingCycle === 'ANNUAL' ? annualTotal : monthlyTotal)
          }
        />
      </div>
    </div>
  );
}

function Total({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="label" style={{ fontSize: 10.5 }}>
        {label}
      </span>
      <span
        className="mono"
        style={{ fontSize: emphasis ? 18 : 15, fontWeight: 600, color: emphasis ? 'var(--leaf)' : undefined }}
      >
        {value}
      </span>
    </div>
  );
}
